import { useState, useEffect } from "react"
import { BarChart2 } from "lucide-react"
import { GoogleSearchPreview, GoogleDisplayPreview, GoogleShoppingPreview } from "components"
import { metaApi } from "api"
import { useAuth } from "components/context/AuthContext"

const PREVIEW_FORMATS = [
  { label: "Mobile Feed", value: "MOBILE_FEED_STANDARD" },
  { label: "Instagram", value: "INSTAGRAM_STANDARD" },
  { label: "Reels", value: "FACEBOOK_REELS_MOBILE" },
  { label: "Facebook Story", value: "FACEBOOK_STORY_MOBILE" },
  { label: "Desktop Feed", value: "DESKTOP_FEED_STANDARD" },
  { label: "Right Column", value: "RIGHT_COLUMN_STANDARD" },
  { label: "Instant Article", value: "INSTANT_ARTICLE_STANDARD" },
  { label: "Messenger Mobile", value: "MESSENGER_MOBILE_INBOX_MEDIA" },
  { label: "Marketplace Mobile", value: "MARKETPLACE_MOBILE" },
]

const PREVIEW_HEIGHTS = {
  MOBILE_FEED_STANDARD: 690,
  INSTAGRAM_STANDARD: 525,
  FACEBOOK_REELS_MOBILE: 300,
  FACEBOOK_STORY_MOBILE: 570,
  DESKTOP_FEED_STANDARD: 700,
  RIGHT_COLUMN_STANDARD: 215,
  INSTANT_ARTICLE_STANDARD: 450,
  MESSENGER_MOBILE_INBOX_MEDIA: 600,
  MARKETPLACE_MOBILE: 390,
}

const DEFAULT_META_FORMATS = ["MOBILE_FEED_STANDARD", "INSTAGRAM_STANDARD"]

const CampaignDetailsAdsTab = ({ platform, data }) => {
  const ads = Array.isArray(data?.ad_content)
    ? data.ad_content
    : data?.adsets?.flatMap((adset) => adset.ads || []) || []

  

  if (!ads || ads.length === 0) {
    return (
      <div className="col-span-full bg-white/10 border border-white/20 rounded-xl p-10 text-center text-white/70">
        No hay anuncios disponibles para esta campaña
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      {ads.map((ad, idx) => (
        <AdCard
          key={ad.id || idx}
          ad={ad}
          platform={platform}
          products={data?.products || []}
          campaignType={data?.campaign_type}
        />
      ))}
    </div>
  )
}

const AdCard = ({ ad, platform, products = [], campaignType }) => {
  const { auth } = useAuth()
  const user = auth?.user || {}
  const isMeta = platform === "meta"

  const getAvailableTypes = () => {
    const types = []
    if (!isMeta) {
      if (campaignType === "SEARCH" && ad.headlines?.[0] && ad.descriptions?.[0]) types.push("SEARCH")
      if (campaignType === "DISPLAY" && ad.image_urls?.length > 0) types.push("DISPLAY")
      if (campaignType === "PERFORMANCE_MAX") {
        if (ad.headlines?.[0] && ad.descriptions?.[0]) types.push("SEARCH")
        if (ad.image_urls?.length > 0) types.push("DISPLAY")
        if (products?.length > 0 || products?.error) types.push("SHOPPING")
      }
      if (!["SEARCH", "DISPLAY", "PERFORMANCE_MAX"].includes(campaignType)) {
        if (ad.type === "RESPONSIVE_DISPLAY_AD" && ad.image_urls?.length > 0) types.push("DISPLAY")
        else if (ad.headlines?.[0] && ad.descriptions?.[0]) types.push("SEARCH")
        if (ad.image_urls?.length > 0 && !types.includes("DISPLAY")) types.push("DISPLAY")
      }
    }
    return types
  }

  const availableTypes = getAvailableTypes()

  const [metaPreviews, setMetaPreviews] = useState(
    DEFAULT_META_FORMATS.map((format) => ({ format, html: ad.creative_preview }))
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isMeta) return
    setLoading(true)
    Promise.all(
      DEFAULT_META_FORMATS.map((format) =>
        metaApi.fetchMetaCreativeById(ad.creative_id, format).then(({ data: creative }) => ({ format, html: creative }))
      )
    )
      .then((results) => setMetaPreviews(results))
      .finally(() => setLoading(false))
  }, [ad.creative_id, isMeta])

  const handleMetaPreviewChange = async (index, newFormat) => {
    const { data: creative } = await metaApi.fetchMetaCreativeById(ad.creative_id, newFormat)
    const updated = [...metaPreviews]
    updated[index] = { format: newFormat, html: creative }
    setMetaPreviews(updated)
  }

  const getGridCols = () => {
    if (isMeta) return metaPreviews.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
    const count = availableTypes.length
    if (count === 1) return "grid-cols-1"
    if (count === 2) return "grid-cols-1 md:grid-cols-2"
    return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
  }

  const maxHeight = isMeta
    ? Math.max(...metaPreviews.map((p) => PREVIEW_HEIGHTS[p.format] || 700))
    : "auto"

  if (!isMeta && availableTypes.length === 0)
    return (
      <div className="bg-white/10 border border-white/20 rounded-xl flex flex-col gap-4 transition-all hover:bg-white/15 p-4">
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center gap-2">
            <img src="/google-ads-icon.png" alt="Google Ads" className="h-4" />
            <h3 className="text-sm font-medium text-white group-hover:text-fuchsia-400 transition pr-6">
              {ad.name || "Anuncio"}
            </h3>
          </div>
          <BarChart2 className="h-4 w-4 text-white/50" />
        </div>
        <div className="text-center text-white/70 p-4">No hay contenido disponible para mostrar preview</div>
      </div>
    )

  return (
    <div className="bg-white/10 border border-white/20 rounded-xl flex flex-col gap-4 transition-all hover:bg-white/15 p-4">
      <div className="flex justify-between items-start mb-1">
        <div className="flex items-center gap-2">
          <img src={isMeta ? "/meta-icon.png" : "/google-ads-icon.png"} alt={`${platform} Ads`} className="h-4" />
          <h3 className="text-sm font-medium text-white group-hover:text-fuchsia-400 transition pr-6">
            {ad.name || "Anuncio"}
          </h3>
        </div>
        <BarChart2 className="h-4 w-4 text-white/50" />
      </div>

      {isMeta ? (
        loading ? (
          <div className="text-center text-white/70 p-4">Cargando previsualización...</div>
        ) : (
          <div className={`grid ${getGridCols()} gap-4 w-full justify-center`}>
            {metaPreviews.map(({ format, html }, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-2 border border-white/20 rounded-lg bg-white/5 p-2"
                style={{ minHeight: maxHeight !== "auto" ? `${maxHeight + 100}px` : "auto" }}
              >
                <select
                  className="w-full rounded-lg border border-white/20 text-sm text-white bg-white/10 p-2 focus:outline-none focus:border-fuchsia-400"
                  value={format}
                  onChange={(e) => handleMetaPreviewChange(idx, e.target.value)}
                >
                  {PREVIEW_FORMATS.map((f) => (
                    <option key={f.value} value={f.value} className="bg-slate-800">
                      {f.label}
                    </option>
                  ))}
                </select>
                <div className="w-full flex justify-center flex-1">
                  <div
                    className="rounded-lg overflow-hidden border border-white/20 bg-white"
                    style={{ height: `${PREVIEW_HEIGHTS[format] || 700}px`, width: "fit-content" }}
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className={`grid ${getGridCols()} gap-4 w-full`}>
          {availableTypes.map((type) => (
            <div
              key={type}
              className="flex flex-col justify-center border border-white/20 rounded-lg bg-white/5 p-2 min-h-fit"
            >
              {type === "SEARCH" && (
                <div className="flex flex-col justify-center h-full">
                  <GoogleSearchPreview
                    title={ad.headlines?.[0]}
                    description={ad.descriptions?.[0]}
                    url={ad.final_urls?.[0] || user?.website_url || "https://www.ejemplo.com"}
                    keywords={ad.keywords?.join(", ")}
                    titles={ad.headlines || []}
                    descriptions={ad.descriptions || []}
                  />
                </div>
              )}
              {type === "DISPLAY" && (
                <GoogleDisplayPreview
                  imageUrl={ad.image_urls?.find((img) => img.type !== "LOGO")?.url}
                  headline={ad.headlines?.[0]}
                  description={ad.descriptions?.[0]}
                  brandName={ad.brand || "Tu Marca"}
                  finalUrl={ad.final_urls?.[0] || user?.website_url || "https://www.ejemplo.com"}
                />
              )}
              {type === "SHOPPING" && <GoogleShoppingPreview products={products} />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CampaignDetailsAdsTab
